import { useEffect } from 'react'
import { Button } from '@/app/common/components/button/Button.tsx'
import Label from '@/common/components/Label.tsx'
import { SubmitButton } from '@/common/components/SubmitButton.tsx'
import Textarea from '@/common/components/Textarea.tsx'
import { InfoCircleIcon } from '@/common/icons/general.tsx'
import { FamilyApplicationQueryKeys } from '@/queries/family_applications/queries.ts'
import {
  ApplicationFlag,
  FamilyApplicationDetails,
} from '@/queries/family_applications/requests.ts'
import { Dialog, DialogContent, DialogTrigger } from '@/shad/ui/dialog.tsx'
import { RadioGroup, RadioGroupItem } from '@/shad/ui/radio-group.tsx'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shad/ui/select.tsx'
import { toast } from '@/shad/ui/toast/use-toast.ts'
import { RootState } from '@/storage/store.ts'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { z } from 'zod'
import {
  DecisionSchema,
  ReasoningInputSchema,
  useSubmitFlagDecision,
  sendChatMessage,
} from '../hooks/useSubmitFlagDecision.tsx'
import { useUserPrivileges } from '../../../../../hook/useUserPrivileges.tsx'
import usePermissionAlert from '../../../../../hook/usePermissionAlert.tsx'
import Image from '../../../../common/components/Image'
import { useAppAlert } from '@/layouts/AppAlertProvider.tsx'
import { flagDocuments, StudentFlags } from './flagDocuments.tsx'

const FormSchema = z.object({
  decision: DecisionSchema,
  reasoning: ReasoningInputSchema,
  justification: z.string().min(1, { message: 'Internal note is required' }),
  public_reply: z.string().nullable().optional(),
})

export type DecisionFormData = z.infer<typeof FormSchema>

interface Props {
  application: FamilyApplicationDetails
  flag: ApplicationFlag
  refetch?: () => void
}

interface Macros {
  title: string
  body: string
  code: string
}

const macros: Array<Macros> = [
  {
    title: 'Accept Messaging',
    body: 'Thank you for submitting the required documentation for {flag}{student}. We have reviewed your submission and are pleased to inform you that your updates have been accepted. Please log in to your account to proceed with your application.',
    code: 'accepted',
  },
  {
    title: 'Reject Messaging',
    body: 'We regret to inform you that we are unable to proceed with your application. The document you have provided in this section{student} does not meet the guidelines to establish {flag}.\nIf you have alternate documentation to support your application, please access your homepage and upload the additional documents for review.',
    code: 'rejected',
  },
  { title: 'Blank Message', body: '', code: '' },
]

function ApplicationDecisionPopup({ flag, refetch, application }: Props) {
  const [open, setOpen] = useState(false)
  const queryClient = useQueryClient()

  const privileges = useUserPrivileges()
  const { showPermissionDeniedMessage } = usePermissionAlert()
  const [selectedMacro, setSelectedMacro] = useState('')
  const { fireAlert } = useAppAlert()

  function getDefaultDecision() {
    if (flag.status === 'Approved') {
      return 'accepted'
    }
    if (flag.status === 'Rejected') {
      return 'rejected'
    }

    return undefined
  }

  const defaultReasoning =
    flag.reasoning_name === 'No selection'
      ? 'Based on evidence'
      : flag.reasoning_name

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    control,
    watch,
  } = useForm<DecisionFormData>({
    defaultValues: {
      decision: getDefaultDecision(),
      reasoning: defaultReasoning,
      justification: flag.justification ?? undefined,
    },
    resolver: zodResolver(FormSchema),
  })
  // @ts-expect-error Need to fix this casting here. t
  const { applicantId } = useParams<{
    applicantId?: string
  }>() as unknown as string
  const { selectedProgramYear } = useSelector((s: RootState) => s.programs)
  const { mutate: submitDecision, status: submitFlagStatus } =
    useSubmitFlagDecision({
      onError: () => {
        toast({
          title: 'Error',
          description: 'There was an error submitting the flag review ',
        })
      },
      onSuccess: () => {
        if (refetch) {
          refetch()
        }
        setOpen(false)
        const id = parseInt(applicantId)
        sendMesssageToApplicant()
        queryClient.invalidateQueries({
          queryKey: FamilyApplicationQueryKeys.details(
            id,
            selectedProgramYear?.id
          ),
        })
        toast({
          title: `Flag was succesfully marked as ${getValues().decision}`,
          description: 'The flag was successfully updated',
        })
      },
    })

  function getStudents() {
    const students = []
    if (Array.isArray(application.students))
      students.push(...application.students)
    else if (application.students) students.push(application.students)

    if (Array.isArray(application.other_students))
      students.push(...application.other_students)
    return students
  }

  const flagStudent = flag.student_id
    ? getStudents().find((student) => student.id === flag.student_id)
    : null

  const sendMesssageToApplicant = () => {
    let message = `Decision: ${getValues().decision}\nReason: ${getValues().reasoning}`
    if (getValues().public_reply) {
      message = `${message}\n${getValues().public_reply}`
    }
    sendChatMessage({ flag_id: flag.id, message })
  }
  const onSubmit = (data: DecisionFormData) => {
    submitDecision({
      flagId: flag.id,
      decision: data.decision,
      justification: data.justification,
      reasoning: data.reasoning,
    })
  }

  const handleClose = () => {
    setOpen(false)
  }

  const hasReviewed = flag.status !== 'Pending'

  const getFlagDocuments = () => {
    if (Object.keys(flagDocuments).includes(flag.flag_code)) {
      const variations = flagDocuments[flag.flag_code]
      if (StudentFlags.includes(flag.flag_code)) {
        return application?.students_documents?.filter((document) =>
          variations.includes(document.variation)
        )
      } else {
        return (
          application?.family_documents?.filter((document) =>
            variations.includes(document.variation)
          ) ?? []
        )
      }
    }
    return []
  }

  watch('reasoning')

  const handleOpenChange = async (open: boolean) => {
    if (!hasReviewed && flag.flag_code.includes('income')) {
      const missingFields: Array<string> = []
      if (!application.income || application.income == 0)
        missingFields.push('Income')
      if (!application.household_filed_federal_income)
        missingFields.push('Income Type')
      if (
        !application.household_inhabitants ||
        application.household_inhabitants == 0
      )
        missingFields.push('Household Size')
      if (missingFields.length > 0) {
        await fireAlert({
          variant: 'error',
          messageComponent: (
            <>
              <p>In order to review please first fill the Household Details.</p>

              <ul role="list" className="mt-2">
                {missingFields.map((item) => (
                  <li key={item} className="list-disc list-inside">
                    Missing {item} field
                  </li>
                ))}
              </ul>
            </>
          ),
        })
        return
      }
    }

    if (open && !privileges.user.applicants_edit) {
      showPermissionDeniedMessage()
      return
    }
    setOpen(open)
  }

  const decision = useWatch({ control, name: 'decision' })
  useEffect(() => {
    const macro = macros.find((macro) => macro.code == decision)
    if (macro) {
      setPublicReply(macro)
      setSelectedMacro(macro.title)
    }
  }, [decision])

  const setPublicReply = (macro: Macros) => {
    let message = macro.body
      .replace('{flag}', flag.flag_name)
      .replace('{student}', flagStudent ? ` for ${flagStudent.name ?? ''}` : '')
    if (macro.title == 'Accept Messaging' && flagStudent)
      message = message.replace(
        `documentation for ${flag.flag_name}`,
        `${flag.flag_name} documentation`
      )
    setValue('public_reply', message)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <div className="flex items-center gap-2">
          {hasReviewed ? (
            <Button variant="outline" size="sm">
              Update
            </Button>
          ) : (
            <Button variant="primary" size="sm">
              Review
            </Button>
          )}
        </div>
      </DialogTrigger>
      <DialogContent
        onInteractOutside={(event) => {
          event.preventDefault()
        }}
        className="max-w-3xl"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* <div className='grid grid-cols-1 sm:grid-cols-2 gap-5'> */}
          {/* <div className='space-y-4'> */}
          <div className="space-y-2">
            <Label>Decision</Label>
            <Controller
              name="decision"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  onValueChange={field.onChange}
                  value={field.value}
                  className="space-y-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="accepted" id="accept" />
                    <Label weight="normal" htmlFor="accept">
                      I <span className="text-green-700">accept</span> the
                      notification made by the applicant
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="rejected" id="reject" />
                    <Label weight="normal" htmlFor="reject">
                      I <span className="text-red-700">reject</span> the
                      notification made by the applicant
                    </Label>
                  </div>
                </RadioGroup>
              )}
            />
            {errors.decision && (
              <p className="text-sm text-red-500">{errors.decision.message}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label>Reasoning</Label>
            <Select
              value={getValues().reasoning}
              onValueChange={(value) => {
                const reasoning = ReasoningInputSchema.parse(value)
                setValue('reasoning', reasoning)
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select reasoning" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={'Based on evidence'}>
                  Based on evidence
                </SelectItem>
                <SelectItem value={'Based on program rules'}>
                  Based on program rules
                </SelectItem>
                <SelectItem value={'Other'}>Other</SelectItem>
              </SelectContent>
            </Select>
            {errors.reasoning && (
              <p className="text-sm text-red-500">{errors.reasoning.message}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label>
              Internal Note
              <span className="text-danger ml-1">*</span>
            </Label>
            <Textarea
              {...register('justification')}
              placeholder="Enter your internal note"
              rows={2}
            />
            {errors.justification && (
              <p className="text-sm text-red-500">
                {errors.justification.message}
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Label>Macros</Label>
            <Select
              value={selectedMacro}
              onValueChange={(value) => {
                setSelectedMacro(value)
                const macro = macros.find((macro) => macro.title == value)
                if (macro) setPublicReply(macro)
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select macro" />
              </SelectTrigger>
              <SelectContent>
                {macros.map((macro, index) => {
                  return (
                    <SelectItem value={macro.title} key={index}>
                      {macro.title}
                    </SelectItem>
                  )
                })}
              </SelectContent>
            </Select>
            {errors.reasoning && (
              <p className="text-sm text-red-500">{errors.reasoning.message}</p>
            )}
          </div>
          <div className="space-y-1">
            <Label>Public Reply</Label>
            <Textarea
              {...register('public_reply')}
              placeholder="Enter your reply"
              rows={6}
            />
          </div>

          {submitFlagStatus === 'error' && (
            <div className="text-sm text-gray-600">
              <InfoCircleIcon className="text-green-600" /> There was an error
              submitting your flag
            </div>
          )}
          <div className="flex gap-2">
            <SubmitButton status={submitFlagStatus} className="" />
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 sm:gap-5 mt-2 sm:mt-8 max-h-16 sm:max-h-48 overflow-auto">
            {getFlagDocuments()?.map((document) => {
              return (
                <Image
                  className="shadow-lg"
                  allowZoom={true}
                  path={document.image_name_front}
                  width={'100%'}
                  alt={document.variation}
                />
              )
            })}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default ApplicationDecisionPopup
